import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import {Document, KeyValueGroup, KeyValueItem} from '@app/shared';
import {
  compareById, deepClone,
  deepGet,
  deepSet,
  detectInterfaceType,
  DocumentService,
  EquipmentService,
  getSafeImageUrl,
  isExpirationAlert,
  LoadUnitService,
  NavigationService,
  OpportunityService,
  ProjectService,
  ScreenService,
  StServiceService
} from '@app/core';
import {catchError, distinctUntilChanged, finalize, Subject, Subscription, takeUntil, throwError} from 'rxjs';
import {ProjectUtilsService} from '../../../features/project/services/project-utils.service';
import {DOCUMENT} from '@angular/common';

@Component({
  selector: 'app-list-key-value',
  standalone: false,
  templateUrl: './list-key-value.component.html',
  styleUrl: './list-key-value.component.less'
})
export class ListKeyValueComponent implements AfterViewInit, OnDestroy {

  deepGet = deepGet;
  deepSet = deepSet;
  compareById = compareById;
  detectInterfaceType = detectInterfaceType;
  getSafeImageUrl = getSafeImageUrl;
  @Input() data: KeyValueGroup[] = [];
  @Input() keysWidth: { value: number; unit: string } | undefined;
  @Input() valuesMaxWidth: { value: number; unit: string } | undefined;
  @Input() innerColumnGap: { value: number; unit: string } | undefined;
  @Input() innerRowGap: { value: number; unit: string } | undefined;
  @Input() pairMaxWidth: { value: number; unit: string } | undefined;
  @Input() expand: boolean = false;
  @Output() dataChanged = new EventEmitter<any>();
  @Output() nestedDataChanged = new EventEmitter<{ value: any, path: string }>();
  @Input() outterColumns: number = 1;
  @Input() outterColumnGap: { value: number; unit: string } | undefined;
  @Input() autoSave: boolean = true;
  @Input() outterRowGap: { value: number; unit: string } | undefined;
  currentEditItem: KeyValueItem | null = null;
  currentEditItemSubscription: Subscription;
  isMobile$;
  mockTabEvent = false;
  protected readonly isExpirationAlert = isExpirationAlert;
  private intersectionObserver!: IntersectionObserver;
  private backupValue: any;
  private altBackupValue: any;
  private destroy$ = new Subject<void>();

  constructor(private elementRef: ElementRef,
              private screenService: ScreenService,
              private projectService: ProjectService,
              private stServiceService: StServiceService,
              private documentService: DocumentService,
              private equipmentService: EquipmentService,
              private loadUnitService: LoadUnitService,
              private opportunityService: OpportunityService,
              private ps: ProjectUtilsService,
              private cdr: ChangeDetectorRef,
              private navigationService: NavigationService,
              @Inject(DOCUMENT) private document: any,
  ) {
    this.isMobile$ = screenService.isMobile$;
    this.currentEditItemSubscription = ps.getCurrentKeyValueItemEditing().subscribe({
      next: (value) => this.currentEditItem = value,
    })
  }

  ngAfterViewInit(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.resize();
            // Desconecta el observer para que no se dispare más de una vez
            this.intersectionObserver.disconnect();
          }
        });
      },
      {threshold: 0.5} // Ajusta el umbral según convenga (0.1 significa que cuando el 10% del elemento es visible)
    );

    this.intersectionObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.currentEditItemSubscription?.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  resize() {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 200);
  }

  shouldShowPlaceholder(item: KeyValueItem, secondaryPath?: string): boolean {
    const value = this.deepGet(item.parentObject, secondaryPath ? secondaryPath : item.path);
    return this.isEmptyValue(value);
  }

  /** Esta es la logica de edicion y guardado de datos **/
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const currentEditableElement = this.elementRef.nativeElement.querySelector('.editable-wrapper.editing');
    const targetElement = event.target as HTMLElement;
    const isDateAndHoursPicker = this.currentEditItem?.type === 'date' && this.currentEditItem.value?.showHours;
    const clickSimulated = !!(event as any).simulatedByTab;
    let datePickerClick;

    if (!isDateAndHoursPicker) {
      datePickerClick = targetElement.className.includes?.('ant-picker')
        && targetElement.className.includes('ant-picker')
        && !targetElement.className.includes('ant-picker-cell-inner');
    } else {
      datePickerClick = targetElement.className.includes?.('ant-picker')
        && targetElement.className.includes('ant-picker')
        && !targetElement.className.includes('ant-picker-ok');
    }

    // Excluir clicks dentro del dropdown de select
    const selectDropdownClick = targetElement.closest('.ant-select-dropdown') !== null;

    if (this.currentEditItem
      && !!currentEditableElement
      && !currentEditableElement.contains(event.target)
      && !clickSimulated
      && !datePickerClick
      && !selectDropdownClick) {

      if (this.autoSave) {
        if (this.currentEditItem.type === 'date' && this.currentEditItem.endDatePath) {
          this.saveChanges(this.currentEditItem, this.currentEditItem.endDatePath, true);
        }
        // Para cuando se modifica el 2do select de un tipo 'route'
        if (this.currentEditItem.type === 'route' && this.currentEditItem.destinationPath) {
          this.saveChanges(this.currentEditItem, this.currentEditItem.destinationPath, true);
        }
        this.saveChanges(this.currentEditItem, this.currentEditItem.path);
      } else {
        // Con autoSave=false, solo resetear el estado de edición para permitir editar otros campos
        this.finalizeSave(false);
      }
    }
  }

  onItemClick(item: KeyValueItem, event: MouseEvent) {
    if (item.editable) {
      // Si hay un campo actualmente en edición y es diferente al que se está clickeando,
      // cancelar la edición del campo anterior (restaurar valor original)
      if (this.currentEditItem && this.currentEditItem !== item) {
        // Restaurar el valor original del campo anterior
        if (this.currentEditItem.path) {
          this.deepSet(this.currentEditItem.parentObject, this.currentEditItem.path, this.backupValue);
        }
        if (this.currentEditItem.type === 'date' && this.currentEditItem.endDatePath) {
          this.deepSet(this.currentEditItem.parentObject, this.currentEditItem.endDatePath, this.altBackupValue);
        }
        if (this.currentEditItem.type === 'route' && this.currentEditItem.destinationPath) {
          this.deepSet(this.currentEditItem.parentObject, this.currentEditItem.destinationPath, this.altBackupValue);
        }
        // Resetear el estado de edición del campo anterior
        this.ps.currentKeyValueItemEditing = null;
        this.backupValue = null;
        this.altBackupValue = null;
      }

      // Si no hay campo en edición o se cambió de campo, iniciar edición del nuevo campo
      if (!this.currentEditItem || this.currentEditItem !== item) {
        this.backupValue = this.deepGet(item.parentObject, item.path);

        // Para cuando se modifica el 2do date-picker de un tipo 'date'
        if (item.type === 'date' && item.endDatePath) {
          this.altBackupValue = this.deepGet(item.parentObject, item.endDatePath);
        }
        // Para cuando se modifica el 2do select de un tipo 'route'
        if (item.type === 'route' && item.destinationPath) {
          this.altBackupValue = this.deepGet(item.parentObject, item.destinationPath);
        }

        this.ps.currentKeyValueItemEditing = item;
      }
    } else {
      event.preventDefault();
    }
  }

  /** Metodo para guardar los datos en la BD **/
  saveChanges(item: KeyValueItem, path: string, isAlt: boolean = false) {

    const altPath = isAlt ? path : undefined;

    if (!item.parentObject?.id || !path) {
      console.error('Datos incompletos para guardar');
      return;
    }

    if (path === 'odooEntityId' && !parseInt(item.parentObject[path])) {
      this.restoreOriginalValues(item, altPath);
      throw new Error(`Propiedad ${path} no es válida`);
    } else if (path === 'odooEntityId' && item.parentObject[path] && parseInt(item.parentObject[path])) {
      item.parentObject[path] = parseInt(item.parentObject[path]);
    }

    // Validar propiedad
    if (!(path in item.parentObject)) {
      this.restoreOriginalValues(item, altPath);
      throw new Error(`Propiedad ${path} no existe`);
    }

    // Si la propiedad se queda vacia y es requerida
    // O si el valor actual es igual al backupValue (no hubo cambios) se restaura y sale de la funcion
    if ((this.isEmptyValue(item.parentObject[path]) && item.required)
      || (isAlt ? this.altBackupValue === item.parentObject[path] : this.backupValue === item.parentObject[path])) {
      this.restoreOriginalValues(item, altPath);
      return;
    }

    // Si es de tipo Date se transforma con toISOString si no se manda directamente el valor
    const value = typeof (item.parentObject[path]) === 'object' && item.parentObject[path]?.toISOString?.()
      ? item.parentObject[path]?.toISOString() || null
      : item.parentObject[path]

    const patchPayload = {
      id: item.parentObject.id,
      [path]: value,
    };

    const detectedType = this.detectInterfaceType(item.parentObject);
    switch (detectedType) {
      case 'LoadUnit':
        // Se llama a uno u otro metodo dependiendo si es LoadUnitTemplate o LoadUnit
        const request = item.isLoadUnitTemplate
          ? this.loadUnitService.patchLoadUnitTemplate(patchPayload)
          : this.loadUnitService.patchLoadUnit(patchPayload)
        request.pipe(
          finalize(() => this.finalizeSave(isAlt)),
          catchError(this.handleUpdateError(item, altPath))
        ).subscribe((rsp) => this.nestedDataChanged.emit({value: rsp?.data, path}));
        break;
      case 'Equipment':
        // this.equipmentService.updateEquipment(patchPayload).pipe(
        //   finalize(() => this.finalizeSave(isAlt)),
        //   catchError(this.handleUpdateError(item, altPath))
        // ).subscribe(() => this.dataChanged.emit(item.parentObject));
        break;
      case 'Document':
        this.documentService.patchDocument(patchPayload).pipe(
          finalize(() => this.finalizeSave(isAlt)),
          catchError(this.handleUpdateError(item, altPath))
        ).subscribe(() => this.dataChanged.emit(item.parentObject));
        break;
      case 'Service':
        this.stServiceService.patchService(patchPayload).pipe(
          finalize(() => this.finalizeSave(isAlt)),
          catchError(this.handleUpdateError(item, altPath))
        ).subscribe(() => {
          this.dataChanged.emit(item.parentObject)
          if (path == 'name') {
            this.navigationService.breadcrumb$.pipe(
              distinctUntilChanged((prev, curr) =>
                prev.length === curr.length &&
                prev[1]?.label === curr[1]?.label),
              takeUntil(this.destroy$)
            ).subscribe(infoRoute => {
              const newBreadcrumb = [...infoRoute];
              newBreadcrumb[2] = {...newBreadcrumb[2], label: item.parentObject[path]};
              this.navigationService.setBreadcrumb(newBreadcrumb);
            });
          }
        });
        break;
      case 'Project':
        this.projectService.patchProject(patchPayload).pipe(
          finalize(() => this.finalizeSave(isAlt)),
          catchError(this.handleUpdateError(item, altPath))
        ).subscribe(() => {
          this.dataChanged.emit(item.parentObject)
          if (path == 'name') {
            this.navigationService.breadcrumb$.pipe(
              distinctUntilChanged((prev, curr) =>
                prev.length === curr.length &&
                prev[1]?.label === curr[1]?.label),
              takeUntil(this.destroy$)
            ).subscribe(infoRoute => {
              const newBreadcrumb = [...infoRoute];
              newBreadcrumb[1] = {...newBreadcrumb[1], label: item.parentObject[path]};
              this.navigationService.setBreadcrumb(newBreadcrumb);
            });
          }
        });
        break;
      case 'ServiceOrder':
        this.stServiceService.patchServiceOrder(patchPayload).pipe(
          finalize(() => this.finalizeSave(isAlt)),
          catchError(this.handleUpdateError(item, altPath))
        ).subscribe((rsp) => this.nestedDataChanged.emit({value: rsp?.data, path}));
        break;
      case 'ServiceOrderTemplate':
        this.stServiceService.patchServiceOrderTemplate(patchPayload).pipe(
          finalize(() => this.finalizeSave(isAlt)),
          catchError(this.handleUpdateError(item, altPath))
        ).subscribe((rsp) => this.nestedDataChanged.emit({value: rsp?.data, path}));
        break;
      case 'ServicePricing':
        this.stServiceService.patchSevicePricing(patchPayload).pipe(
          finalize(() => this.finalizeSave(isAlt)),
          catchError(this.handleUpdateError(item, altPath))
        ).subscribe((rsp) => this.nestedDataChanged.emit({value: rsp?.data, path}));
        break;
      case 'SaleOrder':
        this.opportunityService.updateSaleOrder(patchPayload.id, patchPayload).pipe(
          finalize(() => this.finalizeSave(isAlt)),
          catchError(this.handleUpdateError(item, altPath))
        ).subscribe((rsp) => this.nestedDataChanged.emit({value: rsp?.data, path}));
        break;
      default:
        console.warn('Tipo no soportado:', detectedType);
        this.restoreOriginalValues(item);
    }
  }

  // Funcion para cuando el ngModel es un number pero se muestra un customSelectTemplate con los demas datos del objeto seleccionado
  onObjectToNumberSelectionChange(selectedObject: any, item: any, isAlt: boolean = false) {
    if (selectedObject) {
      if (!isAlt) {
        item.parentObject[item.path] = selectedObject.id;
      } else {
        item.parentObject[item.destinationPath] = selectedObject.id;
      }
    } else {
      item.parentObject[item.path] = null;
    }

    // Terminar la edición automáticamente después de seleccionar un valor
    if (!isAlt) {
      this.finalizeSave(false);
    }
  }

  openLink(document: Document, item: any, event: MouseEvent) {
    // Si no es de link directo es un link de Documento
    if (!item.value.isRawLink) {
      this.documentService.openDocumentInNewTab(document);
      return;
    }

    // Si no es Documento es pq es un link directo
    if ((event.target as any).className.includes('anticon') || (event.target as any).className.includes('link-btn')) {
      window.open(item.value.link, '_blank');
    }

  }

  openOdoo(link: string) {
    // Verificar si el link es válido
    if (link) {
      // Abrir en nueva pestaña
      window.open(link, '_blank');
    } else {
      console.error('El enlace proporcionado no es válido');
    }
  }

  private handleUpdateError(item: KeyValueItem, altPath?: string | undefined) {
    return (e: any) => {
      console.error('Error en actualización:', e);
      this.restoreOriginalValues(item, altPath);
      return throwError(() => e);
    };
  }

  private finalizeSave(isAlt: boolean): void {
    this.backupValue = null;
    if (!isAlt) {
      this.altBackupValue = null;
      this.ps.currentKeyValueItemEditing = null;
    }
    if (this.mockTabEvent) {
      this.generarNuevoTab();
    }
    this.cdr.detectChanges();
  }

  private isEmptyValue(value: any): boolean {
    return value === null || value === undefined || value === '';
  }

  private restoreOriginalValues(item: KeyValueItem, altPath?: string | undefined) {
    if (!!altPath) {
      this.deepSet(item.parentObject, altPath, this.altBackupValue);
    } else if (this.currentEditItem?.path) {
      this.deepSet(item.parentObject, this.currentEditItem.path, this.backupValue);
    }

    this.finalizeSave(!!altPath);
  }

  tabPressed(event: Event) {
    if (this.mockTabEvent) {
      event.preventDefault();
      return;
    }

    // Verificar si la tecla presionada es TAB
    if (((event as KeyboardEvent).key === 'Tab' || (event as KeyboardEvent).keyCode === 9) && this.currentEditItem) {
      // Prevenir el comportamiento por defecto del TAB
      event.preventDefault();

      this.mockTabEvent = true;

      // Aquí procesas tu información
      if (this.autoSave) {
        this.saveChanges(this.currentEditItem, this.currentEditItem.path);
      }
    }
  }

  generarNuevoTab() {
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) return;

    const currentIndex = Array.from(focusableElements).indexOf(this.document.activeElement!);
    let nextIndex = currentIndex + 1;

    if (nextIndex >= focusableElements.length || currentIndex === -1) {
      nextIndex = 0;
    }

    const nextInput = focusableElements[nextIndex] as HTMLElement;

    // Encontrar el contenedor padre (.editable-wrapper)
    const wrapper = this.findEditableWrapper(nextInput);

    this.mockTabEvent = false;

    if (wrapper) {
      // Simular click en el wrapper en lugar del input
      this.simulateWrapperClick(wrapper, nextInput);
    } else {
      // Fallback: enfocar directamente el input
      nextInput.focus();
    }

  }

  private findEditableWrapper(element: HTMLElement): HTMLElement | null {
    // Buscar hacia arriba en el DOM hasta encontrar el contenedor
    let parent = element.parentElement;
    while (parent) {
      if (parent.classList.contains('editable-wrapper')) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  private simulateWrapperClick(wrapper: HTMLElement, input: HTMLElement) {
    // 1. Simular el evento click en el wrapper
    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window
    });

    // 2. Añadir información sobre el item si es necesario
    (clickEvent as any).simulatedByTab = true;

    // 3. Disparar el evento
    wrapper.dispatchEvent(clickEvent);

    // 4. Enfocar el input después de un pequeño retardo
    setTimeout(() => {
      input.focus();

      // Seleccionar toodo el texto si es un input/textarea
      if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
        input.select();
      }
    }, 50);
  }

  private getFocusableElements(): NodeListOf<Element> {
    return this.document.querySelectorAll(`
    button:not([disabled]),
    a[href],
    input:not([disabled]):not([type="hidden"]),
    select:not([disabled]),
    textarea:not([disabled]),
    [tabindex]:not([tabindex="-1"]):not([disabled])
  `);
  }

  isEditing(item: any): {isEditingMode: boolean} {
    return {
      isEditingMode: this.currentEditItem?.label === item.label && this.currentEditItem?.component === item.component
    }
  }

  checkBlurFocus(item: any, evento: any, initialValue: any) {
    evento?.preventDefault?.() ? evento.preventDefault() : null;
    const backupValue = deepClone(initialValue);

    setTimeout(() => {
      if (this.currentEditItem?.label !== item.label) {
        console.log('Se bloqueo el focus pq no esta en modo de edicion');

        // Restaurar el valor original
        this.deepSet(item.parentObject, item.path, backupValue);

        // Obtener el editableWrapper desde el evento
        const targetElement = evento.target as HTMLElement;
        const targetWrapper = targetElement?.closest('.editable-wrapper') as HTMLElement;

        if (targetWrapper) {
          const inputElement = targetWrapper.querySelector('input, textarea, nz-select, nz-date-picker');

          if (inputElement) {
            // Quitar el foco del elemento
            (inputElement as any).blur();

            // Deshabilitar temporalmente el elemento para prevenir escritura
            if (inputElement instanceof HTMLInputElement || inputElement instanceof HTMLTextAreaElement) {
              inputElement.readOnly = true;

              // Re-habilitar después de un breve delay para permitir futuras ediciones
              setTimeout(() => {
                inputElement.readOnly = false;
              }, 100);
            }
          }
        }

        // Forzar la detección de cambios
        this.cdr.detectChanges();
      }
    }, 200);
  }
}
